import React, { useContext } from 'react'
import List from 'components/Common/List/List'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { depositAmountStringBuilder } from 'lib/tours/tourUtils'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { LE_CONTRACTED_HOTEL_TYPES, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR } from 'constants/offer'
import { getMinDepositAmount, isOfferDefaultDepositAmountPercentage } from 'lib/payment/depositsUtils'
import FormatCurrency from '../FormatCurrency'
import GeoContext from 'contexts/geoContext'
import { INELEGIBLE_SERVICE_FEE_OFFER_TYPES } from 'constants/payment'

import { getDefaultDepositAmountPercentage, getDepositServiceFee } from 'checkout/selectors/featureConfig/deposit'
import { useAppSelector } from 'hooks/reduxHooks'
import { TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD } from 'constants/tours'
import { isTourV2OfferType } from 'lib/offer/offerTypes'

// HOW IS THIS DIFFERENT TO getPaymentDueDate IN LIB/DEPOSITUTILS?
function getPaymentDueDaysBeforeCheckin(
  offerType: App.OfferType,
  depositThresholds?: App.DepositThreshold,
) {
  if (depositThresholds?.numberOfDays) {
    return depositThresholds.numberOfDays
  }

  return (offerType == OFFER_TYPE_TOUR || isTourV2OfferType(offerType)) ? TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD : 60
}

function getOfferEligibilityDaysInAdvance(
  offerType: App.OfferType,
  depositThresholds?: App.DepositThreshold,
) {
  if (depositThresholds?.numberOfDays) {
    return depositThresholds.numberOfDays + 1
  }

  return (offerType === OFFER_TYPE_TOUR || isTourV2OfferType(offerType)) ? TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD + 1 : 61
}

function getOfferTypeString(offerType: App.OfferType) {
  switch (offerType) {
    case OFFER_TYPE_TOUR:
    case 'direct_tour':
    case 'partner_tour':
    case 'connection_tour':
      return 'Tour'
    case OFFER_TYPE_ALWAYS_ON:
      return 'Lux Premium Collection'
    default:
      return 'Limited Time LUX Exclusive'
  }
}

export interface Props {
  offerType: App.OfferType;
  offerPackage?: App.Package;
  pkgAvailableRate?: App.OfferAvailableRate;
  checkIn?: moment.Moment;
  depositAmountPercentage?: number
  depositThresholds?: App.DepositThreshold
  depositType?: App.Tours.V2OfferDepositType
  isFlexibleCancellation?: boolean;
  depositAmount?: number;
}

function DepositInfoModalContent({
  offerType,
  depositAmountPercentage,
  depositThresholds,
  depositType,
  isFlexibleCancellation,
  depositAmount,
}: Props) {
  const { currentCurrency } = useContext(GeoContext)
  const offerEligibilityDaysInAdvance = getOfferEligibilityDaysInAdvance(offerType, depositThresholds)
  const paymentDueDaysBeforeCheckin = getPaymentDueDaysBeforeCheckin(offerType, depositThresholds)
  const isHotel = LE_CONTRACTED_HOTEL_TYPES.includes(offerType)
  const minAmount = getMinDepositAmount(currentCurrency)

  const formattedMinimumAmount = <FormatCurrency value={minAmount} format="roundedDollar" currency={currentCurrency} />

  const offerTypeString = getOfferTypeString(offerType)

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)
  const depositServiceFee = useAppSelector(getDepositServiceFee)
  // Enabled for a offer type as long as it is not a connection_tour v2 offer
  const shouldDisplayServiceFeeMessage: boolean = !INELEGIBLE_SERVICE_FEE_OFFER_TYPES.includes(offerType)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const depositAmountString = isOfferUsingDefaultDepositPercentage ?
    'deposit' : !depositType ? `${depositAmountPercentage}% deposit` : `${depositAmountStringBuilder(defaultDepositAmountPercentage, depositType, depositAmountPercentage).short} deposit`

  return (
    <VerticalSpacer gap={16}>
      <BodyText variant="large" as="p">
        {`Just pay a ${depositAmountString} for this offer now, and the rest later `}
        { paymentDueDaysBeforeCheckin } days prior to your check-in date.
      </BodyText>
      <div>
        <Heading variant="heading6">Eligibility</Heading>
        <BodyText variant="large" as="p">
          {offerTypeString} bookings {isHotel && '(excluding flights, experiences, travel protection) '} with
          {minAmount > 0 && <> a value over {formattedMinimumAmount} and</>} travel dates at least{' '}
          { offerEligibilityDaysInAdvance } days in advance.
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">Cancellation policy</Heading>
        <BodyText variant="large" as="p">
          Deposit is non-refundable after free cancellation period. Failure to complete payment will result in the cancellation of your booking,
          {isFlexibleCancellation && ' your deposit less the service fee owed will be defaulted to short-dated credits, expiring 90 days after your booking has been cancelled.'}
          {!isFlexibleCancellation && ' and forfeit of the deposit.'}
        </BodyText>
      </div>
      {isHotel && <BodyText variant="large" as="p">
        Deposit refunds apply only to accommodation. Flights and experiences are subject{' '}
        to the refund policies of the respective operator
      </BodyText>}
      {shouldDisplayServiceFeeMessage && <div>
        <BodyText variant="large" as="p">
          The service fee paid as part of a deposit booking is non-refundable even if cancellation occurs within 7 days of purchase
        </BodyText>
      </div>}
      <BodyText variant="medium" as="p">
        <Heading variant="heading6">
          Changes to booking
        </Heading>
        <BodyText variant="medium" underline as="p">
          Moving booking to an earlier date:
        </BodyText>
        <List type="ul">
          <BodyText variant="medium" as="li">Within 60 days of check-in:</BodyText>
          <List type="ul">
            <BodyText variant="medium" as="li">The entire balance must be paid at the time of making the change, regardless of any price adjustments.</BodyText>
          </List>
        </List>
        <List type="ul">
          <BodyText variant="medium" as="li">More than 60 days from check-in:</BodyText>
          <List type="ul">
            <BodyText variant="medium" as="li">If there is a price change: the entire balance must be paid at the time of the change.</BodyText>
            <BodyText variant="medium" as="li">If there is no price change: you can move the booking without any additional payment.</BodyText>
          </List>
        </List>
        <BodyText variant="medium" underline as="p">
          Moving booking to a future date:
        </BodyText>
        <List type="ul">
          <BodyText variant="medium" as="li">The original deposit remains valid for the new booking.</BodyText>
          <BodyText variant="medium" as="li">If there is a price change: you only pay the difference in price.</BodyText>
          <BodyText variant="medium" as="li">If there is no price change: you can move the booking without any additional payment.</BodyText>
        </List>
      </BodyText>
      {shouldDisplayServiceFeeMessage && <div>
        <Heading variant="heading6">Service Fee</Heading>
        <BodyText variant="large" as="p">
          A service fee of {depositServiceFee}% of the balance of the purchase price payable will be charged at the time of purchase (at the same time the initial {depositAmountString} is paid).
        </BodyText>
      </div>}
    </VerticalSpacer>
  )
}

export default DepositInfoModalContent
